import { useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  Plus,
  Wrench,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  kindLabel,
  useCreateServiceRequest,
  useServiceRequests,
  useUpdateServiceRequestStatus,
  type ServiceRequest,
  type ServiceRequestKind,
  type ServiceRequestStatus,
} from '@/hooks/useServiceRequests';
import {
  useRecentServiceJobs,
  type RecentServiceJob,
} from '@/hooks/useRecentServiceJobs';

const KIND_OPTIONS: ReadonlyArray<{ value: ServiceRequestKind; label: string }> = [
  { value: 'equipment_issue', label: 'Equipment issue' },
  { value: 'refill', label: 'Refill' },
  { value: 'scheduling', label: 'Scheduling' },
  { value: 'other', label: 'Other' },
];

interface ServiceTicketsCardProps {
  customerId: string;
  /** Default location for new tickets — the location the user has selected
   * in the header switcher. Empty string when there's only one location. */
  defaultLocationId: string | null;
}

export function ServiceTicketsCard({
  customerId,
  defaultLocationId,
}: ServiceTicketsCardProps) {
  const { data: tickets = [], isLoading } = useServiceRequests({ onlyOpen: true });
  const { data: recentJobs = [] } = useRecentServiceJobs(90);
  const [composing, setComposing] = useState(false);

  return (
    <div className="space-y-4">
      {/* Recent SF service visits — pulls from Service Fusion (last 90
          days, completed, not invoiced). Helps the customer see what's
          already been done before they file a new ticket. */}
      {recentJobs.length > 0 && (
        <RecentServiceVisits jobs={recentJobs.slice(0, 5)} />
      )}

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-brix-muted">
          Open and in-progress tickets land here. Resolved ones drop off.
        </p>
        {!composing && (
          <Button type="button" size="sm" onClick={() => setComposing(true)}>
            <Plus size={14} /> New ticket
          </Button>
        )}
      </div>

      {composing && (
        <TicketComposer
          customerId={customerId}
          defaultLocationId={defaultLocationId}
          onClose={() => setComposing(false)}
        />
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-brix-muted">
          <Loader2 size={14} className="animate-spin" /> Loading tickets…
        </div>
      ) : tickets.length === 0 ? (
        <p className="text-xs text-brix-muted leading-relaxed">
          No open tickets. New requests post here and route to the service team
          once it's connected.
        </p>
      ) : (
        <ul className="divide-y divide-brix-border -my-2">
          {tickets.map((t) => (
            <TicketRow key={t.id} ticket={t} />
          ))}
        </ul>
      )}
    </div>
  );
}

function TicketRow({ ticket }: { ticket: ServiceRequest }) {
  const update = useUpdateServiceRequestStatus();
  return (
    <li className="py-3 flex items-start gap-3">
      <Wrench size={18} className="text-brix-primary shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-brix-ink truncate min-w-0">
            {ticket.subject}
          </p>
          <select
            value={ticket.status}
            disabled={update.isPending}
            onChange={(e) => {
              const next = e.target.value as ServiceRequestStatus;
              if (next !== ticket.status) {
                update.mutate({ id: ticket.id, status: next });
              }
            }}
            className="shrink-0 text-[10px] tracking-tight font-semibold border border-brix-border bg-brix-surface text-brix-primary rounded px-1.5 py-0.5 cursor-pointer disabled:opacity-50"
            aria-label="Status"
          >
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <p className="text-[11px] text-brix-muted truncate flex items-center gap-1.5 mt-0.5">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] tracking-tight font-semibold bg-brix-surface-alt text-brix-primary border border-brix-border">
            {kindLabel(ticket.kind)}
          </span>
          <span>·</span>
          <span>{formatRelative(ticket.createdAt)}</span>
        </p>
        {ticket.body && (
          <p className="text-xs text-brix-muted mt-1 line-clamp-2">{ticket.body}</p>
        )}
      </div>
    </li>
  );
}

interface ComposerProps {
  customerId: string;
  defaultLocationId: string | null;
  onClose: () => void;
}

function TicketComposer({ customerId, defaultLocationId, onClose }: ComposerProps) {
  const { mutate, isPending, error } = useCreateServiceRequest();
  const [kind, setKind] = useState<ServiceRequestKind>('equipment_issue');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!subject.trim()) return;
    mutate(
      { customerId, locationId: defaultLocationId, kind, subject, body },
      {
        onSuccess: () => {
          setSubject('');
          setBody('');
          onClose();
        },
      },
    );
  }

  const errMsg = error instanceof Error ? error.message : null;

  return (
    <form
      onSubmit={submit}
      className="rounded-xl border border-brix-border bg-brix-surface-alt p-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-brix-ink">New service ticket</p>
        <button
          type="button"
          onClick={onClose}
          className="text-brix-muted hover:text-brix-ink"
          aria-label="Cancel"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] tracking-tight text-brix-muted font-semibold mr-1">
          Kind
        </span>
        {KIND_OPTIONS.map((opt) => {
          const selected = kind === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setKind(opt.value)}
              className={cn(
                'text-xs px-2.5 py-1 rounded-full border transition-colors',
                selected
                  ? 'bg-brix-primary text-white border-brix-primary'
                  : 'bg-brix-surface text-brix-ink border-brix-border hover:bg-brix-surface-alt',
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="ticket-subject">Subject</Label>
        <Input
          id="ticket-subject"
          type="text"
          placeholder="Dispenser leaking, ice machine error, etc."
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={140}
          required
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="ticket-body">Details (optional)</Label>
        <textarea
          id="ticket-body"
          placeholder="Anything the tech should know before they arrive."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          maxLength={1000}
          className="w-full rounded-md border border-brix-border bg-brix-surface px-3 py-2 text-sm placeholder:text-brix-muted/70 focus-visible:outline-none focus-visible:border-brix-primary focus-visible:ring-2 focus-visible:ring-brix-primary/20"
        />
      </div>

      {errMsg && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-brix-primary-tint">
          <AlertCircle size={14} className="text-brix-warning mt-0.5 shrink-0" />
          <p className="text-[11px] leading-relaxed text-brix-ink">{errMsg}</p>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={isPending || !subject.trim()}>
          {isPending ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Submitting…
            </>
          ) : (
            'Submit ticket'
          )}
        </Button>
      </div>

      <p className="text-[10px] text-brix-muted leading-relaxed pt-1">
        Tickets save to your account right away. Auto-routing to the dispatch
        board lights up once service routing is connected.
      </p>
    </form>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return 'just now';
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function RecentServiceVisits({ jobs }: { jobs: RecentServiceJob[] }) {
  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface-alt/40 px-3 py-3">
      <div className="flex items-center gap-1.5 mb-2 text-[10px] tracking-tight font-bold text-brix-primary-deep">
        <CheckCircle2 size={11} /> Recent Brix service visits
      </div>
      <ul className="space-y-1.5">
        {jobs.map((j) => (
          <li
            key={j.jobId}
            className="text-xs text-slate-900 tracking-tight flex items-start gap-2"
          >
            <Clock size={11} className="text-slate-900 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">
                {j.description ?? `Service visit · job #${j.jobId}`}
              </div>
              <div className="text-[10px] text-slate-900/70 mt-0.5">
                {j.completedAt ? formatDate(j.completedAt) : '—'}
                {j.technicianName && ` · ${j.technicianName}`}
                {j.location && ` · ${j.location}`}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
